import {
  ApeironPagingDataGrid,
  NextImage,
  noForwardProps,
  PagingDatasourceResult
} from '@apeiron/temp'
import { ApeironLoadingNow } from '@apeiron/ui'
import { Box, ButtonBase, Collapse, styled, Typography } from '@mui/material'
import { useVipPassContext } from '@src/contexts/battle/demo/VipPassContext'
import useGetBattlePassPrizeHistories from '@src/hooks/graphql/useGetBattlePassPrizeHistories'
import useGetClaimableRewardTypeInfosByRewardName from '@src/hooks/graphql/useGetClaimableRewardTypeInfosByRewardName'
import { ICellRendererParams } from 'ag-grid-community'
import { format, parseISO } from 'date-fns'
import * as R from 'ramda'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  width: 100%;
  height: auto;

  background-color: #262626;

  border-radius: 8px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  position: relative;
`

const Title = styled(Typography)`
  width: 100%;
  height: 50px;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: #4c4c4c;
  border-radius: 12px 12px 0px 0px;

  font-family: var(--font-Inter);
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
`

const DataGrid = styled(ApeironPagingDataGrid)`
  .ag-column-first {
    padding-left: 30px;
  }

  .ag-column-last {
    padding-right: 30px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    .ag-column-first {
      padding-left: 16px;
    }

    .ag-column-last {
      padding-right: 16px;
    }
  }
`

const CollapseButton = styled(ButtonBase, noForwardProps)<CollapseProps>`
  padding: 8px;

  width: 100%;
  height: 50px;

  border-radius: 0px 0px 12px 12px;

  background: ${props =>
    props.__collapsed &&
    'linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 1))'};
`

const Triangle = styled(Box, noForwardProps)<CollapseProps>`
  background-color: white;
  width: 35px;
  height: 18px;

  animation: all 0.3s ease-in-out;

  clip-path: polygon(
    ${props =>
      props.__collapsed ? '100% 100%, 0 100%, 50% 0' : '100% 0, 0 0, 50% 100%'}
  );
`

const DarkOverlay = styled(Box, noForwardProps)<CollapseProps>`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  border-radius: 12px;

  display: ${props => (props.__collapsed ? 'none' : 'block')};

  background: ${props =>
    !props.__collapsed &&
    'linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 1))'};
`

const RewardBox = styled(Box)`
  display: flex;
  align-items: center;

  gap: 6px;
`

const KeyImage = styled(NextImage)`
  aspect-ratio: 1 / 1.5;

  width: 30px;
`

const KeyName = styled(Typography)`
  display: flex;
  align-items: center;
`

const RewardImage = styled(NextImage)`
  aspect-ratio: 1;

  width: 40px;
`

const RewardAmount = styled(Typography)``

const RewardText = styled(Typography)`
  color: rgba(177, 178, 178, 1);
`

const VipPassDrawHistory = () => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)

  const { fetch: getPriceHistory } = useGetBattlePassPrizeHistories()

  const { fetch: getClaimableRewardTypeInfosByRewardName } =
    useGetClaimableRewardTypeInfosByRewardName()

  const { lootBoxData, pagingDataGridRef, currentBattlePass } =
    useVipPassContext()

  const formatDate = useCallback((date: string) => {
    return format(parseISO(date), 'dd MMM yyyy, HH:mm')
  }, [])

  const handleCollapse = useCallback(() => {
    setOpen(prev => !prev)
  }, [])

  const fetch = useCallback(
    async (page: number, pageSize: number): Promise<PagingDatasourceResult> => {
      const response = await getPriceHistory({
        filterInput: { seasonID: currentBattlePass?.seasonID || '' },
        pageInput: {
          page,
          pageSize
        }
      })

      const allAssetsName = R.pluck('prizeAssetName', response.data)

      const allAssets = await getClaimableRewardTypeInfosByRewardName(
        allAssetsName
      )

      const data = response.data.map(item => {
        const boxData = lootBoxData.find(
          lootBox => lootBox.assetName === item.useKeyAssetName
        )

        const rewardData = allAssets?.find(
          asset => asset.assetName === item.prizeAssetName
        )

        return {
          ...item,
          keyUsed: boxData?.keyName,
          keyImage: boxData?.keyImage,
          rewardName: rewardData?.name,
          rewardImage: rewardData?.image,
          drawDate: formatDate(item.drawDate)
        }
      })

      return {
        data,
        total: response.pageInfo.total
      }
    },
    [
      currentBattlePass?.seasonID,
      formatDate,
      getClaimableRewardTypeInfosByRewardName,
      getPriceHistory,
      lootBoxData
    ]
  )

  const columns = useMemo(
    () => [
      {
        field: 'keyUsed',
        headerName: t('vip_pass.draw_history.header.key_used'),
        flex: 1,
        minWidth: 160,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <RewardBox>
              <KeyImage src={params.data.keyImage} />
              <KeyName>{params.data.keyUsed}</KeyName>
            </RewardBox>
          )
        }
      },
      {
        field: 'rewardName',
        headerName: t('vip_pass.draw_history.header.reward'),
        cellClass: 'custom-ag-cell-center',
        headerClass: 'custom-ag-header-center',
        flex: 1,
        minWidth: 300,
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <RewardBox>
              <RewardImage src={params.data.rewardImage} />
              <RewardAmount>{params.data.itemQty}</RewardAmount>
              <RewardText>{params.data.rewardName}</RewardText>
            </RewardBox>
          )
        }
      },
      {
        field: 'drawDate',
        headerName: t('vip_pass.draw_history.header.time'),
        cellClass: 'custom-ag-cell-right',
        headerClass: 'custom-ag-header-right',
        flex: 1,
        minWidth: 170
      }
    ],
    [t]
  )

  if (R.isEmpty(lootBoxData)) {
    return <ApeironLoadingNow />
  }

  return (
    <MainContainer>
      <Title>{t('vip_pass.draw_history.title').toUpperCase()}</Title>

      <Collapse in={open} collapsedSize={100}>
        <DataGrid
          fetch={fetch}
          pageSize={4}
          gridRef={pagingDataGridRef}
          domLayout='autoHeight'
          headerHeight={60}
          rowHeight={60}
          columnDefs={columns}
        />
      </Collapse>

      <DarkOverlay __collapsed={open} />

      <CollapseButton onClick={handleCollapse} __collapsed={open}>
        <Triangle __collapsed={open} />
      </CollapseButton>
    </MainContainer>
  )
}

type CollapseProps = {
  __collapsed: boolean
}

export default VipPassDrawHistory
